import { AxiosResponse } from 'axios';
import { config } from '../config';
import axios from './axios';
import { API, PHRASE_SETS } from './endpoints';

const backendUrl: string = config.backendApiUrl;
const baseUrl = `${backendUrl}/${API}/${PHRASE_SETS}`;

export enum PhraseVisibility {
  PERSONAL = 'PERSONAL',
  GLOBAL = 'GLOBAL',
  SHARED = 'SHARED',
}

export enum PhraseType {
  BOOST = 'BOOST',
  G2A = 'G2A',
}

export interface IPhraseSet {
  id: number;
  isOwner: boolean;
  name: string;
  type: PhraseType;
  visibility: PhraseVisibility;
}

export type PhraseSets = IPhraseSet[];

interface IPostPhraseSet {
  isShared: boolean;
  name: string;
  type: PhraseType;
}

interface IPostPhraseSetResponse {
  id: number;
}

interface IPatchPhraseSet {
  id: number;
  isShared: boolean;
  name: string;
  type: PhraseType;
}

export interface IBoostPhrase {
  id: number;
  text: string;
  boost: number;
}

export type SetPhrases = IBoostPhrase[];

interface IPostBoostPhrase {
  text: string;
  boost: number;
}

interface IPostPhraseResponse {
  id: number;
}

interface ICreateBoostItemsOut {
  id: number;
  items: SetPhrases;
}

export interface IBoostSetUpdateItemsOut {
  id: number;
  toCreateItems: SetPhrases;
  toUpdateItems: SetPhrases;
  toDeleteIndexes: number[];
}

export type LocalBoostSets = { checked: boolean; set: IPhraseSet }[];
export const getPhrases = (): Promise<AxiosResponse<PhraseSets>> => {
  return axios.get(baseUrl + '?set-type=BOOST');
};

export const postPhraseSet = (set: IPostPhraseSet): Promise<AxiosResponse<IPostPhraseSetResponse>> => {
  return axios.post(baseUrl, set);
};

export const deletePhraseSet = (id: number) => {
  return axios.delete(`${baseUrl}/${id}`);
};

export const getPhraseSetById = (id: number): Promise<AxiosResponse<IPhraseSet>> => {
  return axios.get(`${baseUrl}/${id}`);
};

export const patchPhraseSetById = (set: Partial<IPatchPhraseSet>) => {
  const update = { isShared: set.isShared, name: set.name };
  return axios.patch(`${baseUrl}/${set.id}`, update);
};

export const getSetPhrases = (id: number, text?: string): Promise<AxiosResponse<SetPhrases>> => {
  return axios.get(`${baseUrl}/${id}/phrases${text ? `text=${text}` : ''}`);
};

export const postBoostPhrase = (
  setId: number,
  boostedPhrase: IPostBoostPhrase
): Promise<AxiosResponse<IPostPhraseResponse>> => {
  return axios.post(`${baseUrl}/${setId}/phrases`, boostedPhrase);
};

export const createBoostPhraseItems = ({ id, items }: ICreateBoostItemsOut) => {
  const requests = items.map((item) => {
    return postBoostPhrase(id, item);
  });
  return Promise.all(requests);
};

export const deletePhrase = (setId: number, phraseId: number) => {
  return axios.delete(`${baseUrl}/${setId}/phrases/${phraseId}`);
};

export const getPhrase = (setId: number, phraseId: number): Promise<IBoostPhrase> => {
  return axios.get(`${baseUrl}/${setId}/phrases/${phraseId}`);
};

export const updatePhrase = (setId: number, phraseId: number, boostePhrase: Partial<IPostBoostPhrase>) => {
  return axios.put(`${baseUrl}/${setId}/phrases/${phraseId}`, boostePhrase);
};

export const updateBoostSetItems = ({
  id,
  toCreateItems,
  toUpdateItems,
  toDeleteIndexes,
}: IBoostSetUpdateItemsOut) => {
  const createRequests = toCreateItems.map((item) => () => postBoostPhrase(id, item));

  const updateRequests = toUpdateItems.map((item) => () => updatePhrase(id, item.id, item));

  const deleteRequests = toDeleteIndexes.map((item) => () => deletePhrase(id, item));
  const allRequests = deleteRequests.concat(updateRequests).concat(createRequests);

  return allRequests;
};
